import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { Search, Lightbulb, Code2, Rocket, ArrowRight } from 'lucide-react';

const steps = [
  {
    icon: Search,
    title: 'AI Visibility Audit',
    description: 'Provedeme hlubokou analýzu vašeho webu. Zjistíme, jak jste viditelní v ChatGPT, Perplexity a dalších AI nástrojích.',
    color: '#0066FF',
  },
  {
    icon: Lightbulb,
    title: 'Návrh řešení',
    description: 'Navrhneme konkrétní úpravy obsahu a struktury. Každé doporučení má jasný cíl a měřitelný výstup.',
    color: '#00D4AA',
  },
  {
    icon: Code2,
    title: 'Implementace',
    description: 'Upravíme obsah přímo na vašem webu. Zaměříme se na stránky s největším potenciálem.',
    color: '#39FF14',
  },
  {
    icon: Rocket,
    title: 'Monitoring a růst',
    description: 'Sledujeme výsledky a pravidelně reportujeme. Upravujeme strategii podle změn v AI nástrojích.',
    color: '#00A8E8',
  },
];

const Process = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  const { scrollYProgress } = useScroll({
    target: timelineRef,
    offset: ['start center', 'end center'],
  });

  const lineHeight = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);

  return (
    <section
      id="process"
      ref={sectionRef}
      className="py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden"
    >
      {/* Background Glow */}
      <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-[#0066FF]/8 rounded-full blur-[200px] pointer-events-none" />
      <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-[#00D4AA]/8 rounded-full blur-[200px] pointer-events-none" />

      <div className="max-w-5xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card border-[#00A8E8]/30 mb-6"
            whileHover={{ scale: 1.03 }}
          >
            <span className="text-sm text-[#00A8E8] font-medium">Jak to funguje</span>
          </motion.div>

          <h2 className="text-headline font-bold mb-6 text-slate-900">
            Od auditu po <span className="text-gradient">výsledky</span>
          </h2>

          <p className="text-body text-lg max-w-xl mx-auto">
            Transparentní proces, který přináší měřitelné výsledky.
            Víte přesně, co se děje a proč.
          </p>
        </motion.div>

        {/* Timeline */}
        <div ref={timelineRef} className="relative">
          {/* Vertical Line Background */}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#0066FF]/10 via-[#00D4AA]/10 to-[#39FF14]/10 md:-translate-x-1/2" />

          {/* Animated Line */}
          <motion.div
            className="absolute left-8 md:left-1/2 top-0 w-0.5 md:-translate-x-1/2"
            style={{
              height: lineHeight,
              background: 'linear-gradient(to bottom, #0066FF, #00D4AA, #39FF14)',
            }}
          />

          {/* Steps */}
          <div className="space-y-16">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isLeft = index % 2 === 0;

              return (
                <motion.div
                  key={index}
                  className={`relative flex items-center gap-8 ${
                    isLeft ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                  initial={{ opacity: 0, x: isLeft ? -40 : 40 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.12 }}
                >
                  {/* Content */}
                  <div className={`flex-1 ${isLeft ? 'md:text-right' : 'md:text-left'}`}>
                    <motion.div
                      className="glass-card rounded-2xl p-6 inline-block hover:border-[#00D4AA]/30 transition-colors"
                      whileHover={{
                        scale: 1.01,
                        y: -2,
                      }}
                      transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                    >
                      <h3
                        className="text-title font-semibold mb-3"
                        style={{ color: step.color }}
                      >
                        {step.title}
                      </h3>
                      <p className="text-body text-sm max-w-sm">
                        {step.description}
                      </p>
                    </motion.div>
                  </div>

                  {/* Center Node */}
                  <div className="relative z-10 flex-shrink-0">
                    <motion.div
                      className="w-16 h-16 rounded-2xl flex items-center justify-center glass-strong"
                      style={{
                        border: `2px solid ${step.color}50`,
                        boxShadow: `0 0 25px ${step.color}30`,
                      }}
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                    >
                      <Icon className="w-7 h-7" style={{ color: step.color }} />
                    </motion.div>

                    {/* Pulse Ring */}
                    <motion.div
                      className="absolute inset-0 rounded-2xl"
                      style={{ border: `2px solid ${step.color}` }}
                      animate={{
                        scale: [1, 1.25, 1],
                        opacity: [0.4, 0, 0.4],
                      }}
                      transition={{
                        duration: 2.5,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                    />
                  </div>

                  {/* Spacer for alternating layout */}
                  <div className="flex-1 hidden md:block" />
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <motion.div
          className="mt-20 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <Link
            to="/audit"
            className="inline-flex items-center gap-2 text-[#0066FF] hover:text-[#00D4AA] transition-colors font-medium text-lg group"
          >
            Začněte AI Auditem
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default Process;
